import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { DoorlyLogo } from '../../components/DoorlyLogo';
import { cn } from '../../lib/cn';
import { SectionLabel } from '../../components/ui/SectionLabel';

type Doc = 'cgu' | 'mentions';

function readInitialDoc(): Doc {
  if (typeof window === 'undefined') return 'cgu';
  const param = new URLSearchParams(window.location.search).get('doc');
  return param === 'mentions' ? 'mentions' : 'cgu';
}

function syncDocToUrl(doc: Doc): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('doc', doc);
  window.history.replaceState(null, '', url.toString());
}

/**
 * Public legal page — accessible both signed-in (via /legal route) and
 * signed-out (App.tsx short-circuits the auth gate when pathname is /legal).
 * Hosts the two notices required by French law (LCEN 2004-575) : CGU +
 * Mentions Légales. Linked from the "À propos" modal and the sign-in screen.
 *
 * Uses plain URL search params (not nuqs) so it works outside the
 * RouterProvider / NuqsAdapter — i.e. when rendered standalone for
 * signed-out visitors.
 */
export function LegalPage() {
  const [doc, setDocState] = useState<Doc>(readInitialDoc);
  const setDoc = (next: Doc) => {
    setDocState(next);
    syncDocToUrl(next);
  };

  return (
    <div className="min-h-dvh bg-bg text-ink flex flex-col">
      <header className="sticky top-0 z-10 bg-surface/95 backdrop-blur-xl border-b border-hair">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) window.history.back();
              else window.location.href = '/';
            }}
            className="inline-flex items-center gap-1.5 text-body font-semibold text-ink-soft hover:text-ink"
            aria-label="Retour"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </button>
          <DoorlyLogo variant="full" size={28} />
        </div>
        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-3">
          <h1 className="text-title md:text-page font-extrabold tracking-tight text-ink">
            Informations légales
          </h1>
          <div className="mt-3 flex gap-1.5 border-b border-hair">
            <TabButton active={doc === 'cgu'} onClick={() => setDoc('cgu')}>
              CGU
            </TabButton>
            <TabButton active={doc === 'mentions'} onClick={() => setDoc('mentions')}>
              Mentions légales
            </TabButton>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
          {doc === 'cgu' ? <CguContent /> : <MentionsContent />}
        </article>
      </main>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px px-3 md:px-4 py-2 text-body font-bold border-b-2 transition-colors',
        active
          ? 'border-brand text-brand'
          : 'border-transparent text-ink-mute hover:text-ink-soft',
      )}
    >
      {children}
    </button>
  );
}

// ── Document chrome ────────────────────────────────────────────────────

function Section({
  number,
  title,
  children,
}: {
  number?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-8 first:mt-0">
      <h2 className="text-strong md:text-title font-extrabold text-ink flex items-baseline gap-2">
        {number && <span className="text-ink-mute font-mono text-body">{number}.</span>}
        <span>{title}</span>
      </h2>
      <div className="mt-3 space-y-3 text-body leading-relaxed text-ink-soft">
        {children}
      </div>
    </section>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-body font-bold text-ink mb-1.5">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Def({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <p>
      <strong className="text-ink font-bold">« {term} »</strong> {children}
    </p>
  );
}

// ── CGU ────────────────────────────────────────────────────────────────

function CguContent() {
  return (
    <>
      <header className="border-b border-hair pb-5 mb-2">
        <SectionLabel>
          Conditions Générales d'Utilisation
        </SectionLabel>
        <h2 className="mt-1 text-page md:text-kpi font-extrabold text-ink">Doorly</h2>
        <p className="mt-2 text-meta text-ink-mute">Dernière version — 05 mai 2026</p>
      </header>

      <Section number="1" title="Préambule">
        <p>
          L'application <strong className="text-ink">DOORLY</strong> est une application de la
          société WEEDEV. La société WEEDEV, société par actions simplifiées, est spécialisée
          en marketing digital. Son siège social se situe au 114 rue Gallieni, 92100
          Boulogne-Billancourt, elle est immatriculée au Registre des Commerces et des
          Sociétés avec comme numéro SIREN : 820 561 538, dont le Président est Value Invest
          Holding, représentée par son gérant Léo Hauet.
        </p>
      </Section>

      <Section number="2" title="Définitions">
        <Def term="CGU">désigne les présentes Conditions Générales d'Utilisation.</Def>
        <Def term="Application">désigne l'application DOORLY.</Def>
        <Def term="Société">désigne WEEDEV, créateur et propriétaire de l'Application.</Def>
        <Def term="Utilisateur">
          le prospecteur qui utilise l'Application afin de proposer les offres des
          Professionnels partenaires de WEEDEV au Prospect.
        </Def>
        <Def term="Prospect">
          toute personne physique qui est prospectée par l'Utilisateur afin de le mettre en
          contact avec un Professionnel pour l'achat d'un bien ou d'une prestation de
          services, au travers de la mise en relation par WEEDEV.
        </Def>
        <Def term="Professionnel">
          toute personne physique ou morale, publique ou privée, qui agit à des fins entrant
          dans le cadre de son activité commerciale, industrielle, artisanale, libérale ou
          agricole et fait appel à la Société aux fins de mise en relation avec des Prospects
          susceptibles d'être intéressés par les produits ou les services proposés par le
          Professionnel.
        </Def>
        <Def term="Client">
          le Prospect pourra devenir un Client du Professionnel en cas de contrat conclu entre
          eux.
        </Def>
        <Def term="Législation Applicable">
          toute norme ou réglementation se rapportant à l'activité de navigation sur une
          Application en ligne, notamment les règles liées à la propriété intellectuelle et
          celles contenues dans la loi pour la confiance dans l'économie numérique du 21 juin
          2004.
        </Def>
      </Section>

      <Section number="3" title="Objet">
        <p>
          Les présentes CGU ont pour objet de régir les relations entre la Société et les
          Utilisateurs qui accèdent à l'Application et utilisent ses services à des fins de
          prospection. L'utilisation de l'Application emporte acceptation pleine et entière
          des présentes CGU. A cet effet, la Société invite l'Utilisateur à lire attentivement
          les CGU. L'Utilisateur déclare et garantit avoir lu et compris les CGU.
        </p>
      </Section>

      <Section number="4" title="Prestation de la Société">
        <p>
          La Société met à disposition de l'Utilisateur une Application ayant pour but de
          proposer plusieurs types d'offres au Prospect et de le mettre en relation avec un
          Professionnel. Pour cela, l'Utilisateur doit remplir les champs obligatoires de
          l'interface de mise en relation à l'aide des données fournies par le Prospect. Il
          soumet à son consentement la communication de ces données à un Professionnel pour
          lui proposer une offre adaptée à ses besoins.
        </p>
        <p>La liste des Professionnels est disponible dans notre Politique de confidentialité.</p>
      </Section>

      <Section number="5" title="Accessibilité de l'Application">
        <p>
          La Société fera tout son possible pour maintenir l'Application accessible et
          performante sans retard ou difficultés excessives.
        </p>
        <p>
          Toutefois, la Société ne peut pas garantir un fonctionnement sans interruptions ni
          dérangements de l'accès à l'Application. En particulier, la Société peut être amenée
          à réaliser des travaux d'entretien et des mises à jour de son Application pour
          adapter son contenu et/ou intégrer les évolutions des services et des
          fonctionnalités qui y sont proposés, susceptibles d'interrompre temporairement son
          accès, sans que ses interruptions ne soient considérées comme un manquement de la
          Société à ses obligations.
        </p>
        <p>
          La Société s'engage à ce que l'Application soit accessible, mais ne pourra être
          tenue responsable en cas de bug ou d'inaccessibilité résultant d'un fournisseur
          d'accès internet, d'une mauvaise couverture internet ou encore d'un cas de force
          majeure.
        </p>
      </Section>

      <Section number="6" title="Utilisation et navigation licite de l'Application">
        <p>
          L'Utilisateur s'engage à utiliser l'Application de manière licite et à ne pas en
          détourner les fonctionnalités. Il engage sa responsabilité en cas de préjudice causé
          à la Société.
        </p>
        <p>
          La responsabilité de la Société ne pourra pas être engagée pour toute utilisation
          détournée de l'Application par l'Utilisateur, en dehors des fonctionnalités prévues.
        </p>
        <p>
          L'Utilisateur est informé que sa responsabilité pourra être engagée à la hauteur des
          préjudices subis par la Société conformément à l'article 8 des Présentes.
        </p>
      </Section>

      <Section number="7" title="Prix">
        <p>L'accès à l'Application est gratuit pour les Utilisateurs.</p>
      </Section>

      <Section number="8" title="Responsabilité">
        <Sub title="8.1. De la Société">
          <p>
            Elle exécute ses obligations de moyens de bonne foi et ne peut être tenue
            responsable en cas de faute d'un tiers, de l'Utilisateur, du Prospect, du
            Professionnel ou de force majeure.
          </p>
        </Sub>
        <Sub title="8.2. De l'Utilisateur">
          <p>
            L'Utilisateur s'engage à ne pas utiliser l'Application en violation des lois ou
            règlements en vigueur, et notamment sans s'y limiter à ne pas réaliser d'actes de
            parasitisme ou de concurrence déloyale.
          </p>
          <p>
            L'Utilisateur est responsable des dommages, directs et indirects, qui lui seraient
            imputables et notamment en cas de manquement à ses obligations au titre des
            Présentes ou à la Législation Applicable.
          </p>
          <p>
            Par ailleurs, la Société se réserve le droit d'engager la responsabilité civile
            et/ou pénale de l'Utilisateur.
          </p>
          <p>L'Utilisateur reconnaît que :</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              La Société n'est tenue que d'une obligation de moyens pour l'ensemble des
              services qu'elle propose ;
            </li>
            <li>
              La Société décline toute responsabilité pour tout litige intervenant entre le
              Professionnel et l'Utilisateur, quelle qu'en soit la cause.
            </li>
          </ul>
        </Sub>
      </Section>

      <Section number="9" title="Propriété intellectuelle">
        <p>
          L'Application dans son ensemble et chacun des éléments visuels ou sonores qui le
          composent (à savoir notamment les marques, les logos, les dessins et modèles, les
          illustrations, les photographies, les textes, les animations, les vidéogrammes, les
          phonogrammes, les logiciels, codes sources et les bases de données) sont la
          propriété exclusive de la Société et/ou des titulaires des droits de propriété
          intellectuelle concernés. Tous ces éléments sont protégés au titre de la propriété
          intellectuelle et notamment par le droit d'auteur, le droit des marques, et le droit
          des dessins et modèles, le droit des brevets ainsi que des droits sui generis.
        </p>
        <p>
          En conséquence, toute reproduction, communication, téléchargement, modification ou
          utilisation totale ou partielle de l'un de ces éléments appartenant à la Société ou
          à un tiers, pour quelque motif et pour quelque support que ce soit, nécessite
          l'autorisation écrite et préalable de la Société ou de leurs titulaires, sauf
          lorsque la loi l'y autorise. A défaut de cette autorisation, toute représentation
          totale ou partielle de l'Application et des éléments le composant, par quelque
          procédé que ce soit, est interdite et constituerait soit une contrefaçon sanctionnée
          par les articles L.122-4 et suivants du Code de la propriété intellectuelle, soit un
          acte de concurrence déloyale sanctionné par l'article 1240 du Code civil.
        </p>
        <p>
          Les marques de la Société et de ses partenaires, ainsi que les logos figurant sur
          l'Application sont des marques (semi-figuratives ou non) déposées. Toute
          reproduction ou représentation totale ou partielle de ces marques, toute
          modification, toute altération de ces marques et/ou logos sans l'autorisation
          expresse de la Société sont donc prohibées, au sens de l'article L. 713-2 du Code de
          la propriété intellectuelle.
        </p>
        <p>
          L'Utilisateur déclare et garantit être informé qu'en cas de manquement à l'une
          quelconque de ces obligations, il s'expose à des poursuites judiciaires devant les
          juridictions civiles et/ou pénales.
        </p>
      </Section>

      <Section number="10" title="Données personnelles et témoins de connexion">
        <Sub title="10.1. Données personnelles">
          <p>Pour en savoir plus, voir notre Politique de confidentialité.</p>
        </Sub>
      </Section>

      <Section number="11" title="Modifications des CGU">
        <p>
          La Société peut modifier les CGU à tout moment. Les nouvelles CGU s'appliqueront dès
          leur mise en ligne.
        </p>
      </Section>

      <Section number="12" title="Divers">
        <p>
          Si une disposition des CGU est jugée nulle, les autres resteront en vigueur. Le fait
          de ne pas se prévaloir d'une clause n'emporte pas renonciation.
        </p>
      </Section>

      <Section number="13" title="Droit applicable — Litiges">
        <p>
          Les présentes CGU sont soumises au droit français. En cas de litige, l'Utilisateur
          peut s'adresser au service client à l'adresse suivante : 114 rue Gallieni, 92100
          Boulogne-Billancourt ou par mail :{' '}
          <a
            href="mailto:contact@hipto.com"
            className="text-brand font-semibold hover:underline"
          >
            contact@hipto.com
          </a>
          .
        </p>
        <p>
          A défaut de résolution amiable, l'Utilisateur pourra saisir les juridictions
          compétentes (Nanterre, lieu de prestation ou lieu de domicile de l'Utilisateur).
        </p>
      </Section>
    </>
  );
}

// ── Mentions légales ──────────────────────────────────────────────────

function MentionsContent() {
  return (
    <>
      <header className="border-b border-hair pb-5 mb-2">
        <SectionLabel>
          Mentions légales
        </SectionLabel>
        <h2 className="mt-1 text-page md:text-kpi font-extrabold text-ink">Doorly</h2>
        <p className="mt-2 text-meta text-ink-mute">
          Réglementées par la loi n°2004-575 du 21 juin 2004 pour la confiance dans
          l'économie numérique (LCEN).
        </p>
      </header>

      <Section title="Éditeur">
        <p>
          La présente application ainsi que la marque affiliée sont exploitées par{' '}
          <strong className="text-ink">WEEDEV</strong>.
        </p>
        <div className="rounded-[var(--radius-sm)] border border-hair bg-surface p-4 space-y-1.5">
          <p className="font-bold text-ink">WEEDEV</p>
          <p>Siège social : 114, rue Gallieni — 92100 Boulogne-Billancourt</p>
          <p>
            Email :{' '}
            <a
              href="mailto:contact@hipto.com"
              className="text-brand font-semibold hover:underline"
            >
              contact@hipto.com
            </a>
          </p>
          <p>
            Site internet :{' '}
            <a
              href="https://hipto.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand font-semibold hover:underline"
            >
              hipto.com
            </a>
          </p>
          <p>Tél : +33 (0)1 59 13 15 93</p>
        </div>
        <p>
          <strong className="text-ink">Directeur de publication :</strong> WEEDEV, représentée
          par Léo HAUET et Kilian LE MENESTREL, directeurs généraux.
        </p>
        <p>Société par Actions Simplifiée au capital de 237 585,30 euros.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong className="text-ink">SIRET :</strong> 82056153800035
          </li>
          <li>
            <strong className="text-ink">RCS :</strong> Nanterre B 820 561 538
          </li>
          <li>
            <strong className="text-ink">N° TVA intracommunautaire :</strong> FR02820561538
          </li>
          <li>
            <strong className="text-ink">Publication :</strong> 01 mars 2022
          </li>
        </ul>
      </Section>

      <Section title="Hébergeur">
        <p>Cette application est hébergée par :</p>
        <div className="rounded-[var(--radius-sm)] border border-hair bg-surface p-4 space-y-1">
          <p className="font-bold text-ink">Google Cloud Platform (Google LLC)</p>
          <p>1600 Amphitheatre Parkway</p>
          <p>Mountain View, CA 94043 — États-Unis</p>
          <p>
            <a
              href="https://cloud.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand font-semibold hover:underline"
            >
              cloud.google.com
            </a>
          </p>
        </div>
      </Section>

      <Section title="Propriété intellectuelle">
        <p>
          La structure générale de l'application ainsi que les textes, images, sons et vidéos
          la composant, sont la propriété de WEEDEV. Toute représentation et/ou reproduction
          et/ou exploitation partielle ou totale de cette application, par quelque procédé que
          ce soit, sans l'autorisation préalable et par écrit de WEEDEV est strictement
          interdite et serait susceptible de constituer une contrefaçon au sens de l'article
          L.335-2 et suivants du Code de la propriété intellectuelle.
        </p>
      </Section>

      <Section title="Données personnelles">
        <p>
          Des traitements de données à caractère personnel sont réalisés à travers
          l'utilisation de l'application. Pour en savoir plus sur ces traitements, vous pouvez
          consulter la politique de confidentialité.
        </p>
      </Section>

      <Section title="Maintenance">
        <p>
          WEEDEV se réserve le droit de modifier ou d'actualiser ces mentions légales à tout
          moment et sans préavis.
        </p>
      </Section>
    </>
  );
}
